import React, { useEffect, useState } from "react";
import MessengerLayout      from "../../../layout/MessengerLayout";
import LeaveFilters         from "./filters/LeaveFilters";
import ApprovalQueue        from "./queue/ApprovalQueue";
import RequestDetailsDrawer from "./drawer/RequestDetailsDrawer";
import LeaveHeatmap         from "./widgets/LeaveHeatmap";
import StaffingRisk         from "./widgets/StaffingRisk";
import RequestsTab          from "./tabs/RequestsTab";
import BalancesTab          from "./tabs/BalancesTab";
import PolicyTab            from "./tabs/PolicyTab";
import ReportsTab           from "./tabs/ReportsTab";
import AuditLogTab          from "./tabs/AuditLogTab.jsx";
import EmployeeLeaveDrawer  from "./drawer/EmployeeLeaveDrawer";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const TABS = [
  { key: "overview",  label: "Overview"   },
  { key: "requests",  label: "Requests"   },
  { key: "balances",  label: "Balances"   },
  { key: "policy",    label: "Policy"     },
  { key: "reports",   label: "Reports"    },
  { key: "audit",     label: "Audit Log"  },
];

function buildParams(filters) {
  const p = new URLSearchParams({ start: filters.from, end: filters.to });
  if (filters.location   !== "ALL") p.append("location",   filters.location);
  if (filters.department !== "ALL") p.append("department", filters.department);
  if (filters.type       !== "ALL") p.append("type",       filters.type);
  if (filters.status     !== "ALL") p.append("status",     filters.status);
  return p.toString();
}

export default function AdminLeavePage() {
  const [tab, setTab] = useState("overview");

  // Overview state
  const [filters, setFilters] = useState({
    from:       new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10),
    to:         new Date().toISOString().slice(0, 10),
    location:   "ALL", department: "ALL", type: "ALL", status: "ALL",
  });
  const [kpis,     setKpis]     = useState(null);
  const [queue,    setQueue]    = useState([]);
  const [heatmap,  setHeatmap]  = useState([]);
  const [risk,     setRisk]     = useState({ threshold: 80, risky: [] });
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [selected, setSelected] = useState(null); // request details drawer

  // Employee drawer
  const [empDrawer, setEmpDrawer] = useState(null); // { id, name }

  useEffect(() => {
    if (tab !== "overview") return;
    let alive = true;

    const run = () => {
      setLoading(true); setError(null);
      const qs = buildParams(filters);
      Promise.all([
        fetch(`${API_BASE}/api/admin/leave-rules/kpis?${qs}`,          { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API_BASE}/api/admin/leave-rules/queue?${qs}`,          { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API_BASE}/api/admin/leave-rules/heatmap?${qs}`,        { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API_BASE}/api/admin/leave-rules/staffingrisk?${qs}`,  { headers: authHeaders() }).then(r => r.json()),
      ])
        .then(([k, q, h, r]) => {
          if (!alive) return;
          setKpis(k || null);
          setQueue(Array.isArray(q) ? q : []);
          setHeatmap(Array.isArray(h) ? h : []);
          setRisk(Array.isArray(r) ? { threshold: 80, risky: r } : (r || { threshold: 80, risky: [] }));
        })
        .catch(e => { if (alive) setError(e.message); })
        .finally(() => { if (alive) setLoading(false); });
    };

    run();
    const handleKiraAction = (e) => {
      const t = e.detail?.action_type || "";
      if (["leave_apply", "leave_cancel", "leave_approve", "leave_reject"].includes(t)) {
        run();
      }
    };
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => {
      alive = false;
      window.removeEventListener("kira_action_complete", handleKiraAction);
    };
  }, [tab, filters]);

  const act = (action) => async (id, note) => {
    const status = { approve: "APPROVED", reject: "REJECTED", override: "OVERRIDDEN" }[action];
    await fetch(`${API_BASE}/api/admin/leave-rules/request/${id}/${action}`, {
      method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ note }),
    });
    setQueue(prev => prev.map(x => x.id === id ? { ...x, status } : x));
  };

  const openEmployee = (id, name) => setEmpDrawer({ id, name });

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">

        <div>
          <h1 className="text-2xl font-bold text-[#00ff90]">Leave</h1>
          <p className="text-white/40 text-sm mt-0.5">Govern leave policies, approve requests and protect staffing levels</p>
        </div>

        {/* Tab nav */}
        <div className="flex gap-2 flex-wrap border-b border-white/5 pb-1">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`text-xs px-4 py-2 rounded-t-lg border-b-2 transition-colors ${
                tab === t.key
                  ? "border-[#00ff90] text-[#00ff90]"
                  : "border-transparent text-white/40 hover:text-white/70"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview tab */}
        {tab === "overview" && (
          <>
            <LeaveFilters filters={filters} setFilters={setFilters} kpis={kpis} loading={loading} />
            {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <ApprovalQueue
                loading={loading} items={queue}
                onApprove={act("approve")} onReject={act("reject")} onOverride={act("override")}
                onOpen={setSelected}
              />
              <div className="space-y-5">
                <LeaveHeatmap loading={loading} data={heatmap} />
                <StaffingRisk loading={loading} risk={risk} />
              </div>
            </div>
          </>
        )}

        {tab === "requests" && <RequestsTab onOpenEmployee={openEmployee} />}
        {tab === "balances" && <BalancesTab onOpenEmployee={openEmployee} />}
        {tab === "policy"   && <PolicyTab />}
        {tab === "reports"  && <ReportsTab />}
        {tab === "audit"    && <AuditLogTab />}

      </div>

      {/* Request details drawer */}
      <RequestDetailsDrawer
        open={!!selected} request={selected}
        onClose={() => setSelected(null)}
        onApprove={act("approve")} onReject={act("reject")} onOverride={act("override")}
      />

      {/* Employee leave drawer */}
      {empDrawer && (
        <EmployeeLeaveDrawer
          employeeId={empDrawer.id}
          employeeName={empDrawer.name}
          onClose={() => setEmpDrawer(null)}
        />
      )}
    </MessengerLayout>
  );
}